import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { propertyExpenseAPI } from '../../services/api';
import SuccessModal from '../Common/SuccessModal';
import ErrorModal from '../Common/ErrorModal';
import './PropertyExpenseEdit.css';

const PropertyExpenseEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    expenseDescription: '',
    amount: '',
    invoiceNumber: '',
    payingEntity: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    loadExpense();
  }, [id]);

  const loadExpense = async () => {
    try {
      setLoadingData(true);
      const response = await propertyExpenseAPI.getById(id);
      const data = response.data;
      setFormData({
        date: data.date ? new Date(data.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        expenseDescription: data.expenseDescription || '',
        amount: data.amount || '',
        invoiceNumber: data.invoiceNumber || '',
        payingEntity: data.payingEntity || '',
      });
    } catch (err) {
      setErrorMessage(t('propertyExpense.edit.messages.loadError'));
      setShowErrorModal(true);
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.date) {
      newErrors.date = t('propertyExpense.edit.errors.dateRequired');
    }
    if (!formData.expenseDescription.trim()) {
      newErrors.expenseDescription = t('propertyExpense.edit.errors.expenseDescriptionRequired');
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = t('propertyExpense.edit.errors.amountRequired');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        amount: parseFloat(formData.amount),
      };
      await propertyExpenseAPI.update(id, submitData);
      setShowSuccessModal(true);
      setTimeout(() => {
        navigate('/property-expense');
      }, 1000);
    } catch (err) {
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      } else {
        setErrorMessage(t('propertyExpense.edit.messages.updateError'));
        setShowErrorModal(true);
      }
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="page-container">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="form-container">
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-calendar"></i> {t('propertyExpense.edit.fields.date')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-calendar input-icon"></i>
                <input
                  type="date"
                  className={`form-input ${errors.date ? 'is-invalid' : ''}`}
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                />
              </div>
              {errors.date && (
                <div className="error-message">{errors.date}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-dollar-sign"></i> {t('propertyExpense.edit.fields.amount')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-dollar-sign input-icon"></i>
                <input
                  type="number"
                  step="0.01"
                  className={`form-input ${errors.amount ? 'is-invalid' : ''}`}
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  placeholder={t('propertyExpense.edit.placeholders.amount')}
                />
              </div>
              {errors.amount && (
                <div className="error-message">{errors.amount}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-file-alt"></i> {t('propertyExpense.edit.fields.expenseDescription')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-file-alt input-icon"></i>
                <input
                  type="text"
                  className={`form-input ${errors.expenseDescription ? 'is-invalid' : ''}`}
                  name="expenseDescription"
                  value={formData.expenseDescription}
                  onChange={handleChange}
                  placeholder={t('propertyExpense.edit.placeholders.expenseDescription')}
                  maxLength={500}
                />
              </div>
              {errors.expenseDescription && (
                <div className="error-message">{errors.expenseDescription}</div>
              )}
          </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-receipt"></i> {t('propertyExpense.edit.fields.invoiceNumber')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-receipt input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="invoiceNumber"
                  value={formData.invoiceNumber}
                  onChange={handleChange}
                  placeholder={t('propertyExpense.edit.placeholders.invoiceNumber')}
                  maxLength={100}
                />
              </div>
              </div>
            </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-building"></i> {t('propertyExpense.edit.fields.payingEntity')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-building input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="payingEntity"
                  value={formData.payingEntity}
                  onChange={handleChange}
                  placeholder={t('propertyExpense.edit.placeholders.payingEntity')}
                  maxLength={200}
                />
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-cancel"
              onClick={() => navigate('/property-expense')}
            >
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? t('common.loading') : t('propertyExpense.edit.save')}
            </button>
          </div>
        </form>
      </div>

      <SuccessModal
        show={showSuccessModal}
        message={t('propertyExpense.edit.messages.updateSuccess')}
        onClose={() => {
          setShowSuccessModal(false);
        }}
      />

      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />
    </div>
  );
};

export default PropertyExpenseEdit;


